import React, { FC, useEffect, useState } from 'react';
import {
  PageWrapper,
  useShowConfirmationPopup,
  useShowAlertPopup,
  User,
  Lead,
  Customer,
  GenericList,
  Product,
  LoadingAnimation,
  FilterListInput,
  AdditionalLeadListContent,
  Operation,
  ContentBox,
  OperationContent,
  LeadQuestion,
  OperationQuestion,
  QuestionnaireContent,
  Note,
  OperationHistorySendDocument,
  OperationHistoryAddLead,
  LeadContent,
  Attachment,
} from '@dew21/ui';
import axios from 'axios';
import { useParams } from 'react-router';
import styled from 'styled-components';

export const DownloadLink = styled.a`
  &:hover {
    cursor: pointer;
  }
`;


export const OperationDetails: FC = (props) => {
  const showConfirmationPopup = useShowConfirmationPopup();
  const showNotificationBanner = useShowAlertPopup();

  const { id } = useParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [users, setUsers] = useState<User[] | null>(null);
  const [customers, setCustomers] = useState<Customer[] | null>(null);
  const [products, setProducts] = useState<Product[] | null>(null);
  const [operation, setOperation] = useState<Operation | null>(null);

  const fetchUsers = async () => {
    return axios.get('/api/users');
  };

  const fetchProducts = async () => {
    return axios.get('/api/product/all');
  };

  const fetchOperation = async () => {
    return axios.get(`/api/operations/${id}`);
  };

  const fetchCustomers = async () => {
    return axios.get('/api/customers');
  };

  useEffect(() => {
    setIsLoading(true);
    fetchUsers()
      .then((response) => setUsers(response.data))
      .catch((e) => {
        console.log(e);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (users) {
      fetchCustomers()
        .then((response) => setCustomers(response.data))
        .catch((e) => {
          console.log(e);
          setIsLoading(false);
        });
    }
  }, [users]);

  useEffect(() => {
    if (customers) {
      fetchProducts()
        .then((response) => setProducts(response.data))
        .catch((e) => {
          console.log(e);
          setIsLoading(false);
        });
    }
  }, [customers]);

  useEffect(() => {
    if (products) {
      fetchOperation()
        .then((response) => {
          setOperation(response.data);
          setIsLoading(false);
        })
        .catch((e) => {
          console.log(e);
          setIsLoading(false);
        });
    }
  }, [products]);

  return (
    <PageWrapper
      breadCrumb={[{ title: 'Vorgänge', path: '/operations' }, { title: 'Vorgang', path: `/operations/${id}` }]}
      marginal={{
        items: [],
      }}>
      {
        operation ? (
          <OperationContent
            operation={operation}
            products={products || []}
            users={users || []}
            customers={customers || []}
          />
        ) : <p>Vorgang nicht gefunden.</p>
      }
      <br />
      <p>Bedarfsanalyse</p>
      <br />
      {
        operation ? <GenericList
          columns={[{ name: 'name', displayName: 'Bedarfsanalyse', sortable: false }]}
          items={[{ name: 'Basisfragen', questions: operation.questions }, {
            name: 'Beratungsabschluss',
            questions: operation.concludingQuestions,
          }]}
          additionalRenderFn={(data: { name: string, questions: OperationQuestion[] | LeadQuestion[] }) => (
            <QuestionnaireContent questions={data.questions} />)}
          collapsible={true} /> : <p>Keine Fragen gefunden.</p>
      }

      <br />
      <p>Dokumente</p>
      <br />
      {
        ((operation?.history || []).filter((item) => item.type === 'SEND_DOCUMENT').length + (operation?.summaryDocumentId ? 1 : 0) > 0)
          ?
          <GenericList
            columns={[{ name: 'documentName', displayName: 'Dokument', sortable: false }]}
            items={[
              ...((operation?.history || []).filter(({ type }) => type === 'SEND_DOCUMENT')
                .map((item: any) => ({ documentId: item.documentId, documentName: item.documentName }))),
              { documentId: operation?.summaryDocumentId, documentName: 'Gesprächsprotokoll' },
            ].filter(({ documentId }) => Boolean(documentId))}
            additionalRenderFn={({ documentId, documentName }) => <DocumentItem documentId={documentId} />}
            collapsible={true}
          />
          : <p>Keine Dokumente in dieser Beratung gefunden.</p>
      }

      <br />
      <p>Notizen</p>
      <br />
      {
        (operation && operation.notes && operation.notes.length > 0) ? <GenericList
          columns={[{ name: 'subject', displayName: 'Bedarfsanalyse', sortable: false }]}
          items={operation.notes || []}
          additionalRenderFn={(note: Note) => (<p>{note.body}</p>)}
          collapsible={true} /> : <p>Keine Notiz gefunden.</p>
      }

      <br />
      <p>Leads</p>
      <br />
      {
        (operation && operation.history && operation.history.length > 0 && operation.history.filter((item) => item.type === 'ADD_LEAD').length > 0) ?
          <GenericList
            columns={[{
              name: 'lead', displayName: 'lead', sortable: false, formatFn: (value: Lead) => {
                const leads = operation.history.filter((item) => item.type === 'ADD_LEAD') || [];
                // @ts-ignore
                return (<>Lead {leads.findIndex((lead) => lead.lead.id === value.id) + 1}</>);
              },
            }]}
            items={operation.history.filter((item) => item.type === 'ADD_LEAD') || []}
            additionalRenderFn={(item: OperationHistoryAddLead) => (
              <LeadContent lead={item.lead}
                           users={users || []}
                           customers={customers || []}
                           products={products || []}
                           isLeadDetails={true} />)
            }
            collapsible={true} /> : <p>Keine Leads gefunden.</p>
      }

      {
        isLoading && <LoadingAnimation transparent overlay />
      }
    </PageWrapper>
  );
};

const DocumentItem: FC<{ documentId: string }> = ({ documentId }) => {
  const [doc, setDoc] = useState<Attachment | null>(null);

  useEffect(() => {
    axios.get(`/api/attachment/${documentId}`).then(({ data }) => {
      console.log(data);
      if (typeof data === 'object') {
        setDoc(data);
      }
    }).catch((err) => {
      //
    });
  }, [documentId]);

  if (doc) {
    return <DownloadLink onClick={() => {
      const a = document.createElement('a');
      a.href = doc.dataUrl;
      a.download = doc.fileName;
      a.click();
    }}>Anzeigen</DownloadLink>;
  } else {
    return <p>Dokument nicht gefunden.</p>;
  }
};
